import moment from 'moment';

import {TFieldStateType} from 'components/editable-table';
import {DATE_FOR_REQUEST} from 'shared/constants/date-format';
import {TOracleBoolType} from 'shared/types';
import {isObject, showMessage} from 'shared/utils';
import {IRegionDto} from 'store/slices/regions-slice';

import {DefaultRegion} from '../model/constants';
import {
    IDocumentTemplatesTypeResponse,
    IDocumentTypeResponse,
    IDocumentUploadFile,
    IDocumentUploadFileRequest,
    IDocumentUploadSignature,
    IDocumentUploadSignatureRequest,
    IDocumentUploadSignatureResponse,
    IFileAttachInfoResponse,
} from '../model/types';

/**
 * Генерирует объект информации о загружаемом файле на основе переданных параметров.
 *
 * @param {File} file - Загружаемый файл.
 * @param {string} currentSection - Код текущего региона (секции).
 * @param {IRegionDto[]} [regions] - Список доступных регионов (опционально).
 * @param {IFileAttachInfoResponse} [fileAttachInfo] - Информация о прикрепленном файле (опционально).
 * @param {IDocumentTypeResponse} [defaultBranchName] - Информация о ветке по умолчанию (опционально).
 * @param {IDocumentTypeResponse} [docType] - Тип документа (опционально).
 * @param {IDocumentTemplatesTypeResponse[]} [templateTypesOptions] - Доступные шаблоны документа (опционально).
 * @returns {IDocumentUploadFile} - Объект с информацией о файле для загрузки.
 */
export const generateDefaultFileInfo = (
    file: File,
    currentSection: string,
    regions?: IRegionDto[],
    fileAttachInfo?: IFileAttachInfoResponse,
    defaultBranchName?: IDocumentTypeResponse,
    docType?: IDocumentTypeResponse,
    templateTypesOptions?: IDocumentTemplatesTypeResponse[],
): IDocumentUploadFile => {
    const {
        defaultSubSection, defaultSection, defaultBranchId, defaultName, defaultDocTypeId,
    } = fileAttachInfo || {};
    let section;
    if (regions && Array.isArray(regions)) {
        // используем секцию, которая пришла с бэка если она есть в опциях
        section = regions.find(el => el.regionCode === defaultSection);
        if (!section) {
            // иначе она будет текущей
            section = regions.find(el => el.regionCode === currentSection);
        }
    }

    const subSections = section?.subregionList || [];
    const subSection = subSections.length === 1 ? subSections[0] : subSections.find(
        el => el.code === defaultSubSection,
    );

    return {
        fileName: file.name,
        fileSize: `${file.size}`,
        title: defaultName || DefaultRegion.description,
        type: defaultDocTypeId || parseInt(DefaultRegion.id, 10),
        uploadDate: moment().format(DATE_FOR_REQUEST),
        sectionCode: section?.regionCode,
        sectionId: section?.id,
        subsectionCode: subSection?.code,
        subsectionId: subSection?.id,
        file,
        branchId: defaultBranchId || (defaultBranchName ? defaultBranchName.id : undefined),
        typeCode: docType?.lookupCode,
        templatesTypeOptions: templateTypesOptions,
    };
};

/**
 * Генерирует объект информации о подписанном документе на основе ответа от сервера.
 *
 * @param {IDocumentUploadSignatureResponse} document - Ответ сервера с данными о подписанном документе.
 * @returns {Partial<IDocumentUploadSignature>} - Частичный объект с информацией о подписанном документе.
 */
export const generateSignatureInfoFromResponse = (
    document: IDocumentUploadSignatureResponse,
): Partial<IDocumentUploadSignature> => ({
    documentFileId: document.attachmentId,
    documentName: document.docName,
    documentTypeName: document.docType,
    sectionCode: document.docSection,
    status: document.status,
    attachId: +document.attachmentId as number | undefined,
});

/**
 * Генерирует объект информации о подписанном документе на основе переданных данных о документе.
 *
 * @param {Record<string, any>} document - Объект с информацией о документе.
 * @returns {Partial<IDocumentUploadSignature>} - Частичный объект с информацией о подписанном документе.
 */
export const generateSignatureInfoFromDocumentInfo = (
    document: Record<string, any>,
): Partial<IDocumentUploadSignature> => ({
    fileName: document.signatureFileName,
    documentFileName: document?.docFileName,
    documentFileId: document.attachmentId,
    documentName: document.docName,
    documentTypeName: document.docType,
    sectionCode: document.docSection,
    status: document.status,
    attachId: document.attachmentId,
});

/**
 * Генерирует массив объектов запроса для загрузки файлов на основе переданных файлов.
 *
 * @param {IDocumentUploadFile[]} files - Список файлов для загрузки.
 * @param {boolean} [useUploadDate] - Флаг, указывающий, использовать ли дату загрузки (по умолчанию `true`).
 * @param {boolean} [useId] - Флаг, указывающий, использовать ли id файла или файл для загрузки.
 * @param {boolean} [shouldUnzip] - Флаг, указывающий на необходимость разархивации файла перед сохранением.
 * @returns {IDocumentUploadFileRequest[]} - Массив объектов запроса для загрузки файлов.
 */
export const generateUploadFileRequest = (
    files: IDocumentUploadFile[],
    useUploadDate?: boolean,
    useId?: boolean,
    shouldUnzip?: boolean,
): IDocumentUploadFileRequest[] => files.map(documentFile => ({
    fileName: documentFile.fileName,
    documentName: documentFile.title,
    docTypeId: documentFile.type,
    sectionCode: documentFile.sectionCode,
    subsectionCode: documentFile.subsectionCode,
    xmlValidateStatusCode: documentFile.xmlValidateStatusCode ?? undefined,
    branchId: documentFile.branchId,
    startDocDate: useUploadDate === false ? undefined : documentFile.uploadDate || undefined,
    fileUploadInterfaceId: useId === false ? undefined : documentFile.fileUploadInterfaceId || undefined,
    corrNum: documentFile.correctionNumber ?? undefined,
    regNum: documentFile.regNum ?? undefined,
    file: useId === false ? documentFile.file : undefined,
    docUploadTemplateCode: documentFile.templateCode,
    shouldUnzip,
}));

/**
 * Генерирует массив объектов запроса для загрузки подписей на основе переданных файлов подписей.
 *
 * @param {IDocumentUploadSignature[]} signatureFiles - Список файлов подписей для загрузки.
 * @returns {IDocumentUploadSignatureRequest[]} - Массив объектов запроса для загрузки подписей.
 */
export const generateUploadSignRequest = (
    signatureFiles: IDocumentUploadSignature[],
): IDocumentUploadSignatureRequest[] => signatureFiles.map(signFile => ({
    attachId: signFile.attachId ? `${signFile.attachId}` : undefined,
    file: signFile.file,
    fileName: signFile.fileName,
}));

/**
 * Преобразует объект с флагами типа OracleBoolType ('Y' | 'N') в объект формата FieldStateType.
 *
 * Если `object` является объектом, содержащим флаги 'Y' | 'N', он будет преобразован в объект,
 * где ключи останутся без изменений, а значения 'Y' заменятся на `true`, 'N' — на `false`.
 *
 * Если передано невалидное значение (не объект), возвращается объект с двумя полями:
 * - `activated`: булево значение (`true`, если `object` равен 'Y', иначе `false`).
 * - `required`: всегда `false`.
 *
 * @param {Record<string, TOracleBoolType>} object - Объект с флагами, которые нужно нормализовать.
 * @returns {TFieldStateType} - Объект, в котором флаги преобразованы в булевы значения,
 * где ключи соответствуют переданному `object`,
 * а значения преобразуются в `boolean`. Если `object` не является объектом,
 * возвращается объект с полями `activated` и `required`.
 */
export const normalizeFlagForUploadFile = (object?: Record<string, TOracleBoolType> | string): TFieldStateType => {
    if (isObject(object)) {
        return Object.entries(object).reduce<TFieldStateType>(
            (acc, [key, value]) => ({
                ...acc,
                [key]: value === 'Y',
            }),
            {},
        );
    }

    return {
        activated: object === 'Y',
        required: false,
    };
};

/**
 * Удаляет лишнюю часть из имени сущности, если она соответствует определенным шаблонам.
 *
 * @param {string} entityName - Имя сущности, из которого нужно удалить лишнюю часть.
 * @returns {string} - Имя сущности без лишней части (например, `.declaration`, `.registers` и т.д.).
 */
export const removeExtraPartFromEntityName = (entityName: string) => (
    entityName.replace(/\.(declaration|registers|descriptions|directory)/i, '')
);

/**
 * Создает новое имя файла с добавлением номера, если файл с таким именем уже существует в списке.
 * Если имя файла уже содержит номер, то номер увеличивается на 1.
 *
 * @param {any[]} fileList - Список файлов, в котором проверяется существование файла с таким же именем.
 * @returns {(fileName?: string) => string} - Функция, которая возвращает новое имя файла с номером или без него.
 */
export const createNewFileNameToRepeat = (fileList: any[]) => (fileName?: string): string => {
    if (!fileName) return '';

    const shouldAddFileNumber = fileList
        .some((fileInfo: IDocumentUploadFile) => fileInfo.fileName === fileName);
    let changedFileName = fileName;

    if (shouldAddFileNumber) {
        const fileNumberAndFileExtension = /(\()(\d+)(\))(\.[\w\d]{0,6}$)/;
        const shouldIncreaseFileNumber = fileNumberAndFileExtension.test(fileName);
        if (shouldIncreaseFileNumber) {
            changedFileName = fileName
                .replace(
                    fileNumberAndFileExtension,
                    (_: string, bracketLeft, num, bracketRight, fileExtension) => {
                        const newNum = +num + 1;

                        return `${bracketLeft}${newNum}${bracketRight}${fileExtension}`;
                    },
                );

            return createNewFileNameToRepeat(fileList)(changedFileName);
        }

        const fileExtension = /(\.[\w\d]{0,6}$)/;
        changedFileName = changedFileName.replace(
            fileExtension, (_: string, extensionGroup) => `(1)${extensionGroup}`,
        );

        return createNewFileNameToRepeat(fileList)(changedFileName);
    }

    return changedFileName;
};

/**
 * Фильтрует файлы по допустимым расширениям, удаляя файлы с недопустимыми расширениями.
 * Если файл имеет недопустимое расширение, оно добавляется в список сообщений.
 *
 * @param {File[]} files - Массив файлов, которые необходимо отфильтровать.
 * @param {string[]} [unacceptedFileExtensions] - Список недопустимых расширений файлов.
 * @returns {File[]} - Массив файлов, которые прошли фильтрацию по расширениям.
 */
export const filterFilesByExtensions = (files: File[], unacceptedFileExtensions?: string[]) => {
    const unacceptedExtensions: Set<string> = new Set();
    const filteredFiles = files.filter(
        file => {
            const fileExt = `.${file.name.slice((Math.max(0, file.name.lastIndexOf('.')) || Infinity) + 1)}`;
            const acceptable = !unacceptedFileExtensions?.some(
                ext => ext === fileExt || (fileExt === '.' && ext === ''),
            );
            if (!acceptable) {
                unacceptedExtensions.add(fileExt);
            }
            return acceptable;
        },
    );
    if (unacceptedExtensions.size) {
        showMessage({
            message: `Файлы с недопустимым расширениями: ${Array.from(unacceptedExtensions).join(', ')}
             были удалены из списка загружаемых файлов.`,
            isError: true,
        });
    }

    return filteredFiles;
};

/**
 * Извлекает общее количество байтов (размер загружаемого файла) из события прогресса загрузки.
 *
 * @param {ProgressEvent} progressEvent - Событие прогресса загрузки файла.
 * @returns {number} - Общее количество байтов для загрузки. Если размер неизвестен, возвращает 0.
 */
export const getTotalLengthFrom = (progressEvent: ProgressEvent): number => {
    const {total, target} = progressEvent;
    if (total) return total;

    if (target instanceof XMLHttpRequest) {
        return Number(target.getResponseHeader('content-length'))
               || Number(target.getResponseHeader('x-decompressed-content-length'))
               || 0;
    }

    return 0;
};

/**
 * Вычисляет процент загруженного файла на основе события прогресса.
 *
 * @param {ProgressEvent} progressEvent - Событие прогресса загрузки файла.
 * @returns {number} - Процент загруженных данных (от 0 до 100). Если общий размер неизвестен, возвращает 0.
 */
export const getUploadProgressPercent = (progressEvent: ProgressEvent): number => {
    const {loaded} = progressEvent;
    const total = getTotalLengthFrom(progressEvent);

    if (!total) return 0;

    return Number(((loaded / total) * 100).toFixed(2));
};
